import * as Yup from 'yup';
import { buildPasswordSchema, PasswordPolicyMessages } from '@utils/passwordPolicy';

/**
 * Sign-up validation.
 *
 * Payment rules are conditional on the chosen plan, not fixed. `requiresCard` mirrors the
 * selected plan's `requiresPaymentUpfront`, so a card-free plan never trips card
 * validation and a card-required plan cannot be submitted without one. Keying off a
 * dedicated boolean rather than `paymentMethodType` keeps the two questions — "how would
 * you pay" and "must you pay now" — from being conflated.
 */
const requiredWhenCardRequired = (message: string) => ({
  is: true,
  then: (schema: Yup.StringSchema) => schema.required(message),
  otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
});

// Sprint 28, story-143 (AC4). Top-level fields (name/email/password/org choice) are translated
// via the `login` locale section's messages, mirroring login/reset's schema-as-a-function
// pattern. Sprint 28, develop run 3 (review Warning #2 fix): the billing/card sub-schema
// (requiresCard branch) below is now translated too, reusing `firstNameRequired`/
// `lastNameRequired`/`invalidEmail`/`emailRequired` above for the matching billing-contact
// fields rather than duplicating them.
type RegisterMessages = {
  firstNameRequired: string;
  firstNameMinLength: string;
  lastNameRequired: string;
  lastNameMinLength: string;
  invalidEmail: string;
  emailRequired: string;
  passwordRequired: string;
  passwordsDoNotMatch: string;
  confirmPasswordRequired: string;
  selectOption: string;
  selectHowToGetStarted: string;
  organizationNameRequired: string;
  organizationNameMinLength: string;
  selectPlanToContinue: string;
  billingPeriodRequired: string;
  billingAddressRequired: string;
  billingCityRequired: string;
  billingStateFormat: string;
  billingStateRequired: string;
  billingZipRequired: string;
  cardNumberRequired: string;
  cardNumberInvalid: string;
  cardExpirationRequired: string;
  cardExpirationFormat: string;
  cardExpired: string;
  cvvRequired: string;
  cvvFormat: string;
};

export const registerSchema = (t: RegisterMessages, policy: PasswordPolicyMessages) =>
  Yup.object({
    firstName: Yup.string().min(2, t.firstNameMinLength).required(t.firstNameRequired),
    lastName: Yup.string().min(2, t.lastNameMinLength).required(t.lastNameRequired),
    email: Yup.string().email(t.invalidEmail).required(t.emailRequired),
    // Sprint 47, story-233: one shared rule.
    password: buildPasswordSchema(policy, t.passwordRequired),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t.passwordsDoNotMatch)
      .required(t.confirmPasswordRequired),
    organizationType: Yup.string()
      .oneOf(['create', 'join'], t.selectOption)
      .required(t.selectHowToGetStarted),
    organizationName: Yup.string().when('organizationType', {
      is: 'create',
      then: (schema) =>
        schema.min(2, t.organizationNameMinLength).required(t.organizationNameRequired),
      otherwise: (schema) => schema.notRequired(),
    }),

    // No default plan: creating an organization always requires an explicit choice.
    planId: Yup.string().when('organizationType', {
      is: 'create',
      then: (schema) => schema.required(t.selectPlanToContinue),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Mirrors the selected plan's requiresPaymentUpfront; drives every rule below.
    requiresCard: Yup.boolean(),

    billingPeriod: Yup.string().when('requiresCard', {
      is: true,
      then: (schema) =>
        schema
          .oneOf(['MONTHLY', 'ANNUAL'], t.billingPeriodRequired)
          .required(t.billingPeriodRequired),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Billing contact — reuses the top-level name/email messages above; the requirement text
    // is identical regardless of which name/email field it validates.
    billingFirstName: Yup.string().when(
      'requiresCard',
      requiredWhenCardRequired(t.firstNameRequired),
    ),
    billingLastName: Yup.string().when(
      'requiresCard',
      requiredWhenCardRequired(t.lastNameRequired),
    ),
    billingEmail: Yup.string()
      .email(t.invalidEmail)
      .when('requiresCard', requiredWhenCardRequired(t.emailRequired)),

    // Billing address — the backend DTO requires all of these for the NMI vault.
    billingAddress1: Yup.string().when(
      'requiresCard',
      requiredWhenCardRequired(t.billingAddressRequired),
    ),
    billingCity: Yup.string().when('requiresCard', requiredWhenCardRequired(t.billingCityRequired)),
    billingState: Yup.string().when('requiresCard', {
      is: true,
      then: (schema) =>
        schema.matches(/^[A-Z]{2}$/, t.billingStateFormat).required(t.billingStateRequired),
      otherwise: (schema) => schema.notRequired(),
    }),
    billingZip: Yup.string().when(
      'requiresCard',
      requiredWhenCardRequired(t.billingZipRequired),
    ),

    // Card
    ccNumber: Yup.string().when('requiresCard', {
      is: true,
      then: (schema) =>
        schema
          .required(t.cardNumberRequired)
          .test('cardNumber', t.cardNumberInvalid, (value) => {
            if (!value) return false;
            return /^\d{13,19}$/.test(value.replace(/\s/g, ''));
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
    ccExp: Yup.string().when('requiresCard', {
      is: true,
      then: (schema) =>
        schema
          .required(t.cardExpirationRequired)
          .matches(/^\d{2}\/\d{2}$/, t.cardExpirationFormat)
          .test('expiration', t.cardExpired, (value) => {
            if (!value) return false;
            const [month, year] = value.split('/').map(Number);
            return new Date(2000 + year, month - 1) > new Date();
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
    cvv: Yup.string().when('requiresCard', {
      is: true,
      then: (schema) => schema.required(t.cvvRequired).matches(/^\d{3,4}$/, t.cvvFormat),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
